import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { useViewport } from '../hooks/useViewport';

interface UsernameSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (username: string) => void;
  apiBase: string;
}

// Random username generator with large word pool for uniqueness
function generateRandomUsername(): string {
  const adjectives = [
    'Swift', 'Bold', 'Clever', 'Lucky', 'Sharp', 'Brave', 'Wise', 'Quick', 'Mighty', 'Bright',
    'Fierce', 'Noble', 'Wild', 'Calm', 'Steady', 'Silent', 'Golden', 'Silver', 'Iron',
    'Ancient', 'Young', 'Old', 'New', 'Fast', 'Slow', 'Deep', 'High', 'Low', 'Wide',
    'Dark', 'Light', 'Red', 'Blue', 'Green', 'Black', 'White', 'Grey', 'Brown', 'Gold',
    'Sneaky', 'Crafty', 'Sly', 'Proud', 'Humble', 'Gentle', 'Rough', 'Smooth', 'Tough', 'Solid'
  ];
  
  const nouns = [
    'Miner', 'Prospector', 'Digger', 'Hunter', 'Seeker', 'Finder', 'Explorer', 'Ranger', 'Scout', 'Trail',
    'Nugget', 'Vein', 'Claim', 'Strike', 'Lode', 'Ore', 'Gold', 'Silver', 'Gem', 'Jewel',
    'Pick', 'Shovel', 'Pan', 'Sieve', 'Hammer', 'Chisel', 'Drill', 'Lamp', 'Cart', 'Mule',
    'Canyon', 'Mountain', 'Valley', 'River', 'Stream', 'Cave', 'Tunnel', 'Shaft', 'Mine', 'Quarry',
    'Eagle', 'Wolf', 'Bear', 'Fox', 'Hawk', 'Falcon', 'Lion', 'Tiger', 'Panther', 'Lynx'
  ];
  
  const descriptors = [
    'Master', 'Chief', 'Captain', 'Boss', 'King', 'Lord', 'Duke', 'Baron', 'Knight', 'Warrior',
    'Ace', 'Star', 'Elite', 'Pro', 'Veteran', 'Rookie', 'Novice', 'Expert', 'Legend', 'Hero'
  ];
  
  // Use different patterns for variety:
  // Pattern 1: Adjective + Noun (40% chance)
  // Pattern 2: Descriptor + Noun (30% chance)
  // Pattern 3: Adjective + Adjective + Noun (20% chance)
  // Pattern 4: Descriptor + Adjective + Noun (10% chance)
  
  const pattern = Math.random();
  let username = '';
  
  if (pattern < 0.4) {
    // Adjective + Noun
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    username = `${adj}${noun}`;
  } else if (pattern < 0.7) {
    // Descriptor + Noun
    const desc = descriptors[Math.floor(Math.random() * descriptors.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    username = `${desc}${noun}`;
  } else if (pattern < 0.9) {
    // Adjective + Adjective + Noun
    const adj1 = adjectives[Math.floor(Math.random() * adjectives.length)];
    let adj2 = adjectives[Math.floor(Math.random() * adjectives.length)];
    // Avoid same adjective twice
    while (adj2 === adj1) {
      adj2 = adjectives[Math.floor(Math.random() * adjectives.length)];
    }
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    username = `${adj1}${adj2}${noun}`;
  } else {
    // Descriptor + Adjective + Noun
    const desc = descriptors[Math.floor(Math.random() * descriptors.length)];
    const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    username = `${desc}${adj}${noun}`;
  }
  
  return username;
}

export default function UsernameSelectionModal({ isOpen, onClose, onSuccess, apiBase }: UsernameSelectionModalProps) {
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const viewport = useViewport({ mobileBreakpoint: 1024 });
  const isMobileLandscape = !viewport.isPortrait && viewport.isMobile;
  const isPortrait = viewport.isPortrait;

  const handleGenerate = () => {
    setUsername(generateRandomUsername());
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Clear logged out flag so we can log back in
      const { setLoggedOutFlag } = await import('../lib/guest-storage');
      await setLoggedOutFlag(false);

      // Get guest token for authentication - create account if it doesn't exist
      const { getValidGuestToken, createGuestAccount } = await import('../lib/guest-auth');
      let token = await getValidGuestToken();
      
      if (!token) {
        // No account exists yet - create one first
        await createGuestAccount();
        token = await getValidGuestToken();
        if (!token) {
          throw new Error('Failed to create account. Please try again.');
        }
      }

      const response = await fetch(`${apiBase}/profile/set-username`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({ username: username.trim() }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMsg = 'Failed to set username';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.detail) {
            if (typeof errorData.detail === 'string') {
              errorMsg = errorData.detail;
            } else if (errorData.detail.error) {
              errorMsg = errorData.detail.error;
            }
          }
        } catch {
          errorMsg = errorText || 'Failed to set username';
        }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      onSuccess(data.username);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to set username');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-0 z-50 flex ${isMobileLandscape ? 'items-center' : 'items-start'} justify-center`}
          style={{ 
            background: 'rgba(0, 0, 0, 0.7)', 
            padding: isMobileLandscape ? '0.75rem' : '0.5rem',
            paddingTop: isMobileLandscape 
              ? 'calc(env(safe-area-inset-top, 0px) + 0.75rem)' 
              : 'calc(env(safe-area-inset-top, 0px) + 1rem)',
            paddingBottom: isMobileLandscape ? 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' : '0.5rem',
            paddingLeft: isMobileLandscape ? '0.75rem' : '0.5rem',
            paddingRight: isMobileLandscape ? '0.75rem' : '0.5rem'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              e.preventDefault();
              e.stopPropagation();
              // Don't allow closing by clicking backdrop - user must set username
              // Allow closing by clicking backdrop
              onClose();
            }
          }}
        >
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`relative w-full border-4 ${isMobileLandscape ? 'rounded-xl p-4 max-w-[520px] w-[92vw]' : 'rounded-2xl p-6 md:p-8 max-w-[90vw] md:w-[440px]'}`}
            style={{
              background: 'linear-gradient(to bottom, #f7b267 0%, #f4a261 100%)',
              borderColor: '#8b6542',
              boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              maxHeight: isMobileLandscape 
                ? 'calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 1.5rem)'
                : 'calc(100vh - env(safe-area-inset-top, 0px) - 1rem - 0.5rem)',
              minHeight: isMobileLandscape ? 'min(520px, calc(100vh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 1.5rem))' : undefined,
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onClose();
              }}
              className={`absolute right-2 top-2 flex items-center justify-center rounded-full font-bold leading-none text-gray-600 hover:text-white hover:bg-gray-800 transition-colors cursor-pointer ${isMobileLandscape ? 'h-8 w-8 text-xl' : 'h-8 w-8 text-2xl'}`}
              style={{ zIndex: 50, pointerEvents: 'auto' }}
              aria-label="Close"
            >
              ✕
            </button>
            <h2 className={`font-black ${isMobileLandscape ? 'mb-1.5 text-lg' : 'mb-4 text-2xl md:text-3xl'}`} style={{ color: '#3e2615' }}>
              Choose Your Username
            </h2>
            <p className={`${isMobileLandscape ? 'mb-2 text-sm leading-snug' : 'mb-6 text-base'}`} style={{ color: '#3e2615' }}>
              Pick a name to represent you in the mine. Complete registration later to unlock more features. 
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: isMobileLandscape ? '0.75rem' : '1rem' }}>
              <div>
                <label className={`block font-medium ${isMobileLandscape ? 'text-sm mb-1' : 'text-sm mb-1'}`} style={{ color: '#3e2615' }}>
                  Username
                </label>
                <div className={isPortrait ? 'flex flex-col gap-2' : 'flex gap-2'}>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setError(null);
                    }}
                    placeholder="Enter username"
                    maxLength={20}
                    className={`flex-1 outline-none transition-all ${isMobileLandscape ? 'rounded-lg p-2 text-sm' : 'rounded-xl p-3'}`}
                    style={{
                      border: '2px solid #c4a07a',
                      background: '#ffffff',
                      color: '#3e2615',
                      fontSize: '16px' // Prevents zoom on iOS
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#d4a05f'}
                    onBlur={(e) => e.target.style.borderColor = '#c4a07a'}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={handleGenerate}
                    disabled={loading}
                    className={`font-semibold transition-all hover:scale-105 disabled:opacity-50 ${isMobileLandscape ? 'rounded-lg px-4 py-2 text-sm' : viewport.isPortrait ? 'w-full rounded-xl px-4 py-3' : 'rounded-xl px-4 py-3'}`}
                    style={{
                      background: '#fffaeb',
                      color: '#3e2615',
                      border: '2px solid #8b6542',
                    }}
                  >
                    Random
                  </button>
                </div>
                {error && (
                  <p className={`mt-1 ${isMobileLandscape ? 'text-xs' : 'text-sm'}`} style={{ color: '#d32f2f' }}>
                    {error}
                  </p>
                )}
                <p className={`mt-1 ${isMobileLandscape ? 'text-xs' : 'text-xs'}`} style={{ color: '#3e2615', opacity: 0.7 }}>
                  3-20 characters, letters, numbers, dots, and underscores only
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loading || !username.trim()}
                  className={`flex-1 font-bold transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed ${isMobileLandscape ? 'rounded-lg px-5 py-2 text-sm' : 'rounded-lg px-6 py-3 text-lg'}`}
                  style={{
                    background: '#fffaeb',
                    color: '#3e2615',
                    border: '2px solid #8b6542',
                    boxShadow: '0 4px 0 #8b6542',
                  }}
                >
                  {loading ? 'Setting...' : 'Play as Guest'}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
